import { EventType } from "@/analytics/analytics-events";
import { useFileUploadContext } from "@/components/common/file-upload-context/file-uploads-context";
import { useMultiFileUploader } from "@/components/common/file-upload-context/use-file-uploader";
import {
  transformImage,
  useElementFileUploadContext,
} from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { useTiledImageSheetGenerationContext } from "@/components/common/tiled-image-sheet-generation-context";
import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  selectAreaImgSheets,
  selectImportSheetAreaId,
  selectNumberOfSheets,
} from "@/store/modes/import-sheet-mode-slice";
import { selectIsProjectEmpty } from "@/store/selections-selectors";
import { store } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FileUploadTask, isFileUploadTask } from "@/utils/background-tasks";
import { parseImageFromFile, resizeImage } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  assert,
  generateGUID,
  GUID,
  removeExtension,
} from "@faro-lotv/foundation";
import {
  IElement,
  ImgSheetLevelsOfDetail,
  ImgSheetLevelsOfDetailSource,
} from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { groupBy, isEqual } from "es-toolkit";
import * as PDFJS from "pdfjs-dist";
import { PDFPageProxy } from "pdfjs-dist";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isSupportedImgSheetFileExtension,
  SourcePdfInfoNoPageNumber,
  useCreateAreaImgSheets,
  verifyInputName,
} from "./create-area-utils";
import { GenerateTilesIterator } from "./generate-tiles-iterator";
import { RelativeCrop } from "./image-crop";
import {
  getPredicatedPromise,
  NB_OF_TILES_TO_UPLOAD_BY_PROCESS,
  PredicatedPromiseData,
  TilesGenerationCoordinator,
  TilesUploadStats,
} from "./tiles-generation-coordinator";
import { useDefaultGrid } from "./use-default-grid";
import { useParseUserImage } from "./use-parse-user-image";
import { WindowExtractionSequencer } from "./window-extraction-sequencer";

/** The size of the images that should be extracted from PDF when too big to be obtained with one call */
const EXTRACTION_WINDOW_SIZE = 8192;

/** The threshold size after which the image extraction from PDF will be done using extraction window size */
const EXTRACTION_WINDOW_THRESHOLD = EXTRACTION_WINDOW_SIZE;

/** Function type to get the uploaded data of the original PDF file */
export type GetSourcePdfFileData = () => Promise<
  SourcePdfInfoNoPageNumber | undefined
>;

/**
 * Data structure used to pass multiple files to upload along with their tile coordinates.
 */
type TilesUploadData = {
  /** The x-coordinate of the tile in the image sheet. */
  x: number;
  /** The y-coordinate of the tile in the image sheet. */
  y: number;
  /** The tile image file to upload. */
  file: File;
};

type CreateAreaLogic = {
  /** The original file selected by user for image sheet extraction */
  selectedFile?: File;

  /** The file containing the preview image of the user selected file */
  previewImageFile?: File;

  /** Name of the Area and ImgSheet to create */
  inputName: string;

  /** The validation error to show for the input name */
  inputNameError?: string;

  /** True if the default grid is available */
  hasDefaultGrid: boolean;

  /** True, if the image is larger than supported limits */
  isImageOversized: boolean;

  /** Set the name of the new area */
  setInputName(name: string): void;

  /**
   * Function that creates and add an image sheet tiled
   *
   * @param pdfFile - The PDF file to extract the image from
   * @param resolution - The resolution in pixels of the image sheet tiled
   * @param tilesSize - The size of the tiles in pixels
   * @param selectedPdfPage - The page number to extract from the PDF file
   * @param getSourcePdfUploadedData - Function to get the uploaded data of the original PDF file
   */
  createImageSheetTiled(
    pdfFile: File,
    resolution: number,
    tilesSize: number,
    sheetName: string,
    selectedPdfPage?: number,
    getSourcePdfUploadedData?: GetSourcePdfFileData,
  ): Promise<void>;

  /**
   * Function that creates and add a new sheet (regular or tiled) based on the selected file
   *
   * @param selectedFile - The file selected by the user
   * @param sheetName - The name of the sheet to create
   * @param pageNumber - The page number to extract from the PDF file or undefined if the file is not a PDF
   * @param getSourcePdfUploadedData - Function to get the uploaded data of the original PDF file
   */
  createSheet(
    selectedFile: File,
    sheetName: string,
    pageNumber: number | undefined,
    getSourcePdfUploadedData?: GetSourcePdfFileData,
  ): Promise<void>;

  /** Function that resets the input file and cancels the upload if the user selects so in the dialog */
  cancelUpload(): Promise<void>;

  /** Function to set the current file */
  setFile(file?: File): void;

  /** Function to set the default grid as the current floor image */
  setDefaultGrid(): void;

  /** total number of pages in PDF document; zero if file is not *.pdf */
  numberOfPdfPages?: number;

  /**
   * callback used to update selection of the page from multi-pages PDF file
   *
   * @param pageNumber selected page number (numerated from 1 to numberOfPdfPages)
   */
  onUpdateSelectedPdfPage(pageNumber: number): void;

  /** selected page number if file is PDF (numerated from 1 to numberOfPdfPages). Ignored if file is not PDF */
  selectedPdfPage: number;

  /** ratio width/height of original image - used to scale rotated image in preview  */
  widthToHeightRatio: number;

  /** rotation angle in degrees  */
  rotation: number;

  /**
   * method to set rotation angle
   *
   * @param rotation rotation angle in degrees
   */
  setRotation(rotation: number): void;

  /** crop parameters (in % of original image) */
  crop?: RelativeCrop;

  /**
   * method to set crop parameters
   *
   * @param crop crop parameters (in % of original image)
   */
  setCrop(crop?: RelativeCrop): void;

  /** The area to import sheets to. Undefined when new areas should be created with the sheet. */
  existingArea?: IElement;
};

/** Equivalent to @type{ImgSheetLevelsOfDetail} but without the level field */
type ImgSheetLevelsOfDetailNoLevel = Omit<ImgSheetLevelsOfDetail, "level">;

/**
 * Computes the scale factor to use to get the wanted resolution based on the given width and height
 *
 * @param width - The width of an image or a canvas in pixels
 * @param height - The height of an image or a canvas in pixels
 * @param resolution - The wanted resolution in pixels
 * @returns The scale factor to use to get the wanted resolution
 */
function computeScale(
  width: number,
  height: number,
  resolution: number,
): number {
  return width > height ? resolution / width : resolution / height;
}

/**
 * Computes the total number of tiles to upload for a given PDF page .
 *
 * This function calculates the number of tiles required to represent a PDF page
 * at different resolutions, starting from the specified resolution and halving it
 * iteratively until it is smaller than the tile size.
 *
 * @param page - The PDF page from which an image with the specified resolution should be extracted.
 * @param resolution - The highest resolution in pixels of the image extracted from the PDF page.
 * @param tilesSize - The size of each tile in pixels. Must be equal to or greater than 1.
 * @param rotation - The rotation angle of the page in degrees.
 * @param crop - An optional object specifying the relative crop dimensions and position.
 * @returns The total number of tiles to upload across all resolutions.
 * @throws Will throw an error if `tilesSize` is less than 1.
 */
function computeNbOfAllTilesToUpload(
  page: PDFPageProxy,
  resolution: number,
  tilesSize: number,
  rotation: number,
  crop?: RelativeCrop,
): number {
  assert(
    tilesSize >= 1,
    "Invalid tiles' size. The size must be equal or greater than 1",
  );
  const viewport = page.getViewport({ scale: 1, rotation });
  let nbOfAllTilesToUpload = 0;

  for (
    let currentResolution = resolution;
    currentResolution >= tilesSize;
    currentResolution /= 2
  ) {
    const scale = computeScale(
      viewport.width,
      viewport.height,
      currentResolution,
    );
    const scaledViewport = page.getViewport({ scale, rotation });
    const finalWidth = crop
      ? crop.width * 0.01 * scaledViewport.width
      : scaledViewport.width;
    const finalHeight = crop
      ? crop.height * 0.01 * scaledViewport.height
      : scaledViewport.height;
    const totalNbOfTilesX = Math.ceil(finalWidth / tilesSize);
    const totalNbOfTilesY = Math.ceil(finalHeight / tilesSize);
    nbOfAllTilesToUpload += totalNbOfTilesX * totalNbOfTilesY;
  }

  return nbOfAllTilesToUpload;
}

// Convert the ImgSheetLevelsOfDetailResolution to ImgSheetLevelsOfDetail
function convertToLevelsOfDetail(
  allLevelsData: ImgSheetLevelsOfDetailNoLevel[],
): ImgSheetLevelsOfDetail[] {
  function compareLevels(
    a: ImgSheetLevelsOfDetailNoLevel,
    b: ImgSheetLevelsOfDetailNoLevel,
  ): number {
    return Math.max(a.dimX, a.dimY) - Math.max(b.dimX, b.dimY);
  }
  return allLevelsData.sort(compareLevels).map((levelData, index) => ({
    level: index,
    ...levelData,
  }));
}

/**
 *  Generates tile images from a canvas up to a specified maximum number of tiles.
 *
 * @param maxNbOfTiles  The maximum number of tiles to generate.
 * @param canvas The source canvas from which tiles will be generated.
 * @param canvasPosition The position in the canvas from where tiles will be generated.
 * @param iterator An iterator that provides the coordinates for each tile.
 * @param signal AbortSignal to cancel the operation if needed.
 * @param resolution The resolution of the image being processed.
 * @param tilesSize The size of each tile in pixels.
 * @returns A promise that resolves to an array of TilesUploadData containing the generated tile images.
 */
async function generateTileImagesCapped(
  maxNbOfTiles: number,
  canvas: HTMLCanvasElement,
  canvasPosition: { positionX: number; positionY: number },
  iterator: GenerateTilesIterator,
  signal: AbortSignal,
  resolution: number,
  tilesSize: number,
): Promise<TilesUploadData[]> {
  const tilesUploadDataPromise: Array<() => Promise<TilesUploadData>> = [];
  while (iterator.hasNext() && tilesUploadDataPromise.length < maxNbOfTiles) {
    const { x, y } = iterator.next();
    tilesUploadDataPromise.push(() => {
      if (signal.aborted) {
        return Promise.reject(new Error("Tile generation aborted"));
      }
      const tileCanvas = document.createElement("canvas");
      tileCanvas.width = tilesSize;
      tileCanvas.height = tilesSize;
      const tileContext = tileCanvas.getContext("2d");
      assert(tileContext, "Unable to create a 2d context for the tile canvas");
      tileContext.drawImage(
        canvas,
        x,
        y,
        tilesSize,
        tilesSize,
        0,
        0,
        tilesSize,
        tilesSize,
      );
      return new Promise<TilesUploadData>((resolve, reject) => {
        tileCanvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const tilePositionX = Math.floor(
              (canvasPosition.positionX + x) / tilesSize,
            );
            const tilePositionY = Math.floor(
              (canvasPosition.positionY + y) / tilesSize,
            );
            resolve({
              x: tilePositionX,
              y: tilePositionY,
              file: new File(
                [blob],
                `not_cropped_image_resolution_${resolution}_tile_${tilePositionX}_${tilePositionY}.png`,
                { type: "image/png" },
              ),
            });
          } else {
            reject(new Error("Unable to convert tile to image"));
          }
        }, "image/png");
      });
    });
  }

  if (signal.aborted) {
    throw new Error("Tile generation aborted");
  }

  return await Promise.all(tilesUploadDataPromise.map((fn) => fn()));
}

/**
 * Retrieves an extraction window from a PDF page based on the specified parameters.
 *
 * @param page - The PDF page proxy object representing the page to extract from.
 * @param rotation - The rotation angle of the page in degrees.
 * @param scale - The scale to apply to viewport before extraction.
 * @param extractionWindowSize - The size of the extraction window in pixels.
 * If the size of the windows is too big, the function will throw an error.
 * The error will be thrown by the browser because most of them can't handle canvas with a size
 * bigger than 16K by 16K.
 * @param indexX - The index in X for the extraction window to retrieve.
 * @param indexY - The index in Y for the extraction window to retrieve.
 * @param offsetX - The offset in X for the extraction window to retrieve.
 * @param offsetY - The offset in Y for the extraction window to retrieve.
 * @param sequencer - An optional WindowExtractionSequencer to ensure sequential extraction.
 * @param crop - An optional object specifying the relative crop dimensions and position.
 * Note that the rotation is done before the cropping is applied.
 * @returns The canvas element for rendering the extracted content.
 */
async function getExtractionWindow(
  page: PDFPageProxy,
  rotation: number,
  scale: number,
  extractionWindowSize: number,
  indexX: number,
  indexY: number,
  offsetX: number,
  offsetY: number,
  sequencer?: WindowExtractionSequencer,
  crop?: RelativeCrop,
): Promise<HTMLCanvasElement> {
  // Notify the sequencer that a new window extraction is starting
  sequencer?.startWindowExtraction();

  // Create a canvas for each tile
  const canvas = document.createElement("canvas");
  const canvasContext = canvas.getContext("2d");
  assert(
    canvasContext,
    "Unable to create an offscreen 2d context to render the pdf image",
  );
  const scaledViewport = page.getViewport({
    scale,
    rotation,
  });

  // Calculate the offset for the tile
  const imageOffsetX = indexX * extractionWindowSize;
  const imageOffsetY = indexY * extractionWindowSize;

  const finalWidth = crop
    ? crop.width * 0.01 * scaledViewport.width
    : scaledViewport.width;
  const finalHeight = crop
    ? crop.height * 0.01 * scaledViewport.height
    : scaledViewport.height;

  let remainingImageWidth = finalWidth - imageOffsetX;
  remainingImageWidth =
    remainingImageWidth > 0
      ? remainingImageWidth
      : extractionWindowSize + remainingImageWidth;

  let remainingImageHeight = finalHeight - imageOffsetY;
  remainingImageHeight =
    remainingImageHeight > 0
      ? remainingImageHeight
      : extractionWindowSize + remainingImageHeight;

  assert(
    remainingImageWidth > 0 && remainingImageHeight > 0,
    "Trying to extract a window outside the image",
  );

  canvas.width = Math.min(remainingImageWidth, extractionWindowSize);
  canvas.height = Math.min(remainingImageHeight, extractionWindowSize);

  // Render the PDF page into the canvas context
  await page.render({
    canvasContext,
    viewport: scaledViewport,
    transform: [
      1,
      0,
      0,
      1,
      -(offsetX + imageOffsetX),
      -(offsetY + imageOffsetY),
    ],
  }).promise;

  return canvas;
}

/**
 * Crops a given HTMLCanvasElement based on the specified relative crop dimensions.
 *
 * @param canvas - The canvas element to be cropped.
 * @param crop - An optional object specifying the relative crop dimensions and position.
 *               If not provided, the original canvas is returned unmodified.
 *               - `x`: The x-coordinate of the top-left corner of the crop area as a percentage (0-100).
 *               - `y`: The y-coordinate of the top-left corner of the crop area as a percentage (0-100).
 *               - `width`: The width of the crop area as a percentage of the original canvas width (0-100).
 *               - `height`: The height of the crop area as a percentage of the original canvas height (0-100).
 * @returns A new HTMLCanvasElement containing the cropped portion of the original canvas.
 * @throws Will throw an error if the 2D context for the cropped canvas cannot be created.
 */
function cropCanvas(
  canvas: HTMLCanvasElement,
  crop?: RelativeCrop,
): HTMLCanvasElement {
  if (!crop) {
    return canvas;
  }
  const croppedCanvas = document.createElement("canvas");
  const croppedCanvasContext = croppedCanvas.getContext("2d");
  assert(
    croppedCanvasContext,
    "Unable to create a 2d context for the cropped canvas",
  );
  croppedCanvas.width = Math.floor(canvas.width * crop.width * 0.01);
  croppedCanvas.height = Math.floor(canvas.height * crop.height * 0.01);

  croppedCanvasContext.drawImage(
    canvas,
    Math.floor(canvas.width * crop.x * 0.01),
    Math.floor(canvas.height * crop.y * 0.01),
    croppedCanvas.width,
    croppedCanvas.height,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height,
  );
  return croppedCanvas;
}

type ComputedScaledSize = {
  /** The scale factor to apply to PDF page to get the values stored in width and height fields*/
  scale: number;
  /** The width of the PDF page in pixels when the scale is applied */
  width: number;
  /** The height of the PDF page in pixels when the scale is applied */
  height: number;
};

/**
 * Computes the required scale factor to apply to a PDF page to get a desired resolution.
 * It also computes the final size (width and height) of that PDF page for the specified rotation and resolution.
 *
 * @param page - The PDF page for which computation should be done.
 * @param rotation - The clockwise rotation angle of the page in degrees.
 * @param resolution - The desired resolution of the page in pixels.
 * @returns An object containing the required scale factor to apply to the PDF and size of the PDF page at that desired resolution.
 */
function computeScaledSize(
  page: PDFPageProxy,
  rotation: number,
  resolution: number,
): ComputedScaledSize {
  const viewport = page.getViewport({ scale: 1, rotation });
  const scale = computeScale(viewport.width, viewport.height, resolution);
  const scaledViewport = page.getViewport({ scale, rotation });
  return {
    scale,
    width: scaledViewport.width,
    height: scaledViewport.height,
  };
}

function reduceCanvasSizeToHalf(
  canvasToResize: HTMLCanvasElement,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = canvasToResize.width * 0.5;
  canvas.height = canvasToResize.height * 0.5;
  const canvasContext = canvas.getContext("2d");
  assert(canvasContext, "Unable to create a 2d context for the canvas");
  canvasContext.drawImage(canvasToResize, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * @returns the internal state and mutations for the create area logic
 */
export function useCreateAreaLogic(): CreateAreaLogic {
  const dispatch = useAppDispatch();
  const existingAreaId = useAppSelector(selectImportSheetAreaId);
  const existingArea = useAppSelector(selectIElement(existingAreaId));
  const createAreaImgSheets = useCreateAreaImgSheets();
  const isProjectEmpty = useAppSelector(selectIsProjectEmpty);

  // File upload and task tracking
  const { singleFileUploadManager } = useFileUploadContext();
  const { uploadAreaImageFile, uploadAreaImageSheetTiled } =
    useElementFileUploadContext();
  const [sectionAreaId, setSectionAreaId] = useState<GUID>();
  const imgSheetTiledUploadStep = useRef(1);
  const nbTilesUploaded = useRef(0);
  const {
    startTaskInStore: startTiledImageSheetGenerationTask,
    updateTaskInStore: updateTiledImageSheetGenerationTask,
    completeTask: completeTiledImageSheetGenerationTask,
    failTask: failTiledImageSheetGenerationTask,
    cancelTask: cancelTiledImageSheetGenerationTask,
  } = useTiledImageSheetGenerationContext();

  const windowExtractionSequencer = useMemo(
    () => new WindowExtractionSequencer(),
    [],
  );

  const tilesGenerationCoordinator = useMemo(
    () => new TilesGenerationCoordinator(windowExtractionSequencer),
    [windowExtractionSequencer],
  );

  // Ui Helpers
  const { handleErrorWithDialog, handleErrorWithToast } = useErrorHandlers();

  // Floor image state
  const defaultGrid = useDefaultGrid();
  const {
    previewImageFile,
    isImageOversized,
    extractPreviewImage,
    numberOfPdfPages,
    widthToHeightRatio,
    extractImageFromFile,
  } = useParseUserImage();

  const { sheetFile } = useDataManagementContext();

  // preserve selection of the file to be used when user change only page (for PDF)
  const [selectedFile, setSelectedFile] = useState<File>();

  // Define the state variables for the current PDF page selection in dropdown
  const [selectedPdfPage, setSelectedPdfPage] = useState(1);

  const onUpdateSelectedPdfPage = useCallback(
    (pageNumber: number) => {
      Analytics.track(
        existingArea
          ? EventType.importSheetActivateThumbnail
          : EventType.importAreaActivateThumbnail,
      );
      setSelectedPdfPage(pageNumber);
      extractPreviewImage(selectedFile, pageNumber);
    },
    [existingArea, extractPreviewImage, selectedFile],
  );

  // Area name state
  const [inputName, setInputNameInternal] = useState("");
  const [inputNameError, setInputNameError] = useState<string>();

  // Stores the rotation that user has selected to apply to the preview image displayed to him.
  // The final rotation applied will take into account the initial rotation of the image (the PDF page may be rotated by default).
  const [selectedRotation, setSelectedRotation] = useState(0);

  // crop of the image
  const [crop, setCrop] = useState<RelativeCrop>();

  // when selected file changed, reset rotation
  useEffect(() => {
    setSelectedRotation(0);
  }, [selectedFile]);

  // Validate area name every time it changes
  const setInputName = useCallback((name: string) => {
    setInputNameInternal(name);
    setInputNameError(verifyInputName(name));
  }, []);

  // Track the task for the file upload of the Sheet Image
  // This will take the first ongoing task available for the element
  // NOTE: this will have to be updated when it will be allowed to add a new Sheet Image to a non empty project
  const tasks = useAppSelector(
    (state) =>
      selectBackgroundTasks(
        (task): task is FileUploadTask =>
          task.iElementId === sectionAreaId && isFileUploadTask(task),
      )(state),
    isEqual,
  );

  // Reset the workflow to the initial state (no image, no area name)
  const resetToInitialState = useCallback(() => {
    extractPreviewImage(undefined);
    setInputName("");
    setInputNameError(undefined);
  }, [extractPreviewImage, setInputName]);

  const completeSheetUpload = useCallback(
    async (sourcePdfInfo?: SourcePdfInfoNoPageNumber) => {
      const numberOfSheets = selectNumberOfSheets(store.getState());
      const areaImgSheets = selectAreaImgSheets(store.getState());
      if (numberOfSheets === areaImgSheets.length) {
        await createAreaImgSheets(
          [...areaImgSheets],
          existingArea,
          sourcePdfInfo,
        );
        return;
      }
      if (!isProjectEmpty) dispatch(changeMode("sheet"));
    },
    [createAreaImgSheets, dispatch, existingArea, isProjectEmpty],
  );

  const uploadMultiFiles = useMultiFileUploader();
  const { coreApiClient: coreApi, projectApiClient: projectApi } =
    useApiClientContext();

  /**
   * Uploads a tile image to the server.
   *
   * @param areaId - The ID of the area where to upload the tile.
   * @param projectToken - The bearer token for authenticated requests to projectApi
   * @param tilesFile - An array of objects containing the tile image files to upload and their coordinates.
   * @returns A promise that resolves to a {@link ImgSheetLevelsOfDetailSource} that contains the tile's coordinates and download URL.
   */
  const uploadTilesImage = useCallback(
    async (
      areaId: GUID,
      projectToken: string,
      tilesFile: TilesUploadData[],
    ): Promise<ImgSheetLevelsOfDetailSource[]> =>
      await new Promise<ImgSheetLevelsOfDetailSource[]>((resolve, reject) => {
        uploadMultiFiles({
          files: tilesFile.map((t) => t.file),
          uploadElementType: UploadElementType.areaImageTiled,
          projectId: projectApi.projectId,
          coreApiClient: coreApi,
          iElementId: areaId,
          projectToken,
          onUploadCompleted: (_, uploadFilesData) => {
            // The uploadFilesData have the same size and the same order as the files passed to uploadMultiFiles
            resolve(
              uploadFilesData.map((data, index) => ({
                x: tilesFile[index].x,
                y: tilesFile[index].y,
                source: data.downloadUrl,
              })),
            );
          },
          onUploadFailed: reject,
        });
      }),
    [uploadMultiFiles, projectApi, coreApi],
  );

  /**
   * Generates tiles from a canvas and uploads them to the server.
   * As the tiles are uploaded after their generation, this avoids to keep to much tiles in memory.
   *
   * @param canvas - The canvas element to generate tiles from.
   * @param tilesSize - The size of each tile in pixels.
   * @param positionX - The x-coordinate in pixels of the tile position in the cropped image.
   * @param positionY - The y-coordinate in pixels of the tile position in the cropped image.
   * @param areaId - The ID of the area where to upload the tiles.
   * @param resolution - The resolution in pixels for which the tiles are generated. There could be multiple canvases
   * @param taskID - The ID of the background task for the generation of tiled image sheet.
   * @param signal - An AbortSignal to cancel the tile generation.
   * @param projectToken - The bearer token for authenticated requests to projectApi
   * @param coordinator - The TilesGenerationCoordinator to use to manage tile generation and uploads.
   * It is called with an array of {@link ImgSheetLevelsOfDetailSource} containing the coordinates and download URL of each tile.
   * @throws Will throw an error if the tile generation is aborted.
   * for the same resolution if the image is too big to be extracted in one go.
   */
  const generateTilesFromCanvas = useCallback(
    async (
      canvas: HTMLCanvasElement,
      tilesSize: number,
      positionX: number,
      positionY: number,
      areaId: GUID,
      resolution: number,
      taskID: GUID,
      signal: AbortSignal,
      projectToken: string,
      coordinator: TilesGenerationCoordinator,
    ) => {
      const uploadTilesPromises: Array<
        Promise<ImgSheetLevelsOfDetailSource[]>
      > = [];

      const currentRunStats: TilesUploadStats = new Proxy(
        {
          totalProcessesRan: 0,
          currentOngoingProcesses: 0,
          nbOfUploadTilesPromises: 0,
        },
        {
          set: (target: TilesUploadStats, property: string, value: number) => {
            handleTilesUploadStatsUpdate(target, property, value);
            return true;
          },
        },
      );

      const allOngoingProcessesCompleted: PredicatedPromiseData = {
        promise: null,
        resolver: null,
        fulfilled: false,
        predicate: () => currentRunStats.currentOngoingProcesses === 0,
      };

      const allUploadTilesPromisesCreated: PredicatedPromiseData = {
        promise: null,
        resolver: null,
        fulfilled: false,
        predicate: () =>
          currentRunStats.totalProcessesRan ===
          currentRunStats.nbOfUploadTilesPromises,
      };

      /**
       * Handles updates to the TilesUploadStats properties.
       *
       * @param target The TilesUploadStats object being updated.
       * @param property The name of the property being updated.
       * @param value The new value for the property.
       */
      function handleTilesUploadStatsUpdate(
        target: TilesUploadStats,
        property: string,
        value: number,
      ): void {
        if (
          property === "totalProcessesRan" ||
          property === "currentOngoingProcesses" ||
          property === "nbOfUploadTilesPromises"
        ) {
          target[property] = value;
          // Notify the relevant promise resolvers of the change
          switch (property) {
            case "currentOngoingProcesses":
              allOngoingProcessesCompleted.resolver?.();
              break;
            case "totalProcessesRan":
            case "nbOfUploadTilesPromises":
              allUploadTilesPromisesCreated.resolver?.();
              break;
            default:
              throw new Error(
                `Unknown property ${property} in TilesUploadStats`,
              );
          }
        }
      }

      const iterator = new GenerateTilesIterator(
        { width: canvas.width, height: canvas.height },
        tilesSize,
      );

      coordinator.addStats(currentRunStats);
      let generationLoopRanAtLeastOnce = false;
      while (iterator.hasNext()) {
        generationLoopRanAtLeastOnce = true;
        await coordinator.getStartUploadProcessPromise();
        currentRunStats.currentOngoingProcesses++;
        currentRunStats.totalProcessesRan++;
        generateTileImagesCapped(
          NB_OF_TILES_TO_UPLOAD_BY_PROCESS,
          canvas,
          { positionX, positionY },
          iterator,
          signal,
          resolution,
          tilesSize,
        )
          .then((tilesData) => {
            currentRunStats.currentOngoingProcesses--;
            coordinator.setOnGoingUploads(
              coordinator.getOnGoingUploads() + tilesData.length,
            );
            const uploadPromise = uploadTilesImage(
              areaId,
              projectToken,
              tilesData,
            )
              .then((data) => {
                nbTilesUploaded.current += data.length;
                updateTiledImageSheetGenerationTask(
                  taskID,
                  nbTilesUploaded.current * imgSheetTiledUploadStep.current,
                );
                return data;
              })
              .catch((error) => {
                handleErrorWithToast({
                  error,
                  title: "An error occurred while uploading tile images.",
                });
                throw error;
              })
              .finally(() => {
                coordinator.setOnGoingUploads(
                  coordinator.getOnGoingUploads() - tilesData.length,
                );
              });
            uploadTilesPromises.push(uploadPromise);
            currentRunStats.nbOfUploadTilesPromises =
              uploadTilesPromises.length;
          })
          .catch((error) => {
            handleErrorWithToast({
              error,
              title: "An error occurred while generating tile images.",
            });
            currentRunStats.currentOngoingProcesses--;
          });
      }

      assert(
        generationLoopRanAtLeastOnce,
        "The tile generation loop did not run at least once",
      );

      // Wait for all ongoing processes to be completed
      await getPredicatedPromise(allOngoingProcessesCompleted, () => {
        coordinator.windowExtractionSequencer.setWindowExtractionAsCompleted();
        coordinator.runResolvers();
      });

      if (signal.aborted) {
        throw new Error("Tile generation aborted");
      }

      // Wait for all the promises to be put in the uploadTilesPromises array
      await getPredicatedPromise(allUploadTilesPromisesCreated);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (signal.aborted) {
        throw new Error("Tile generation aborted");
      }

      // The following asserts ensure that all expected upload promises have been created
      // and properly stored in the uploadTilesPromises array. It also validates that the values
      // used to validate the predicated promises were accurate.
      assert(
        uploadTilesPromises.length === currentRunStats.nbOfUploadTilesPromises,
        "Mismatch in the number of tile upload promises",
      );
      // This assert ensures that all processes that were started have completed their uploads.
      assert(
        uploadTilesPromises.length === currentRunStats.totalProcessesRan,
        "Mismatch in the number of total processes ran",
      );
      coordinator.removeStats(currentRunStats);
      const allImageSources = await Promise.all(uploadTilesPromises);
      return allImageSources.flat();
    },
    [
      handleErrorWithToast,
      updateTiledImageSheetGenerationTask,
      uploadTilesImage,
    ],
  );

  // Create a sheet with the current workflow state
  const createSheet = useCallback(
    async (
      file: File,
      sheetName: string,
      pageNumber: number | undefined,
      getSourcePdfUploadedData: GetSourcePdfFileData,
    ) => {
      const inputNameError = verifyInputName(sheetName);
      if (inputNameError) {
        setInputNameError(inputNameError);
        return;
      }

      // generate a new UUID as new area ID if existingAreaId does not exist
      const sectionAreaId = existingAreaId ?? generateGUID();

      setSectionAreaId(sectionAreaId);

      const sheetImage = await extractImageFromFile(file, pageNumber);

      /**
       *  Uploads the area image sheet file.
       */
      async function performAreaImageSheetUpload(): Promise<void> {
        assert(sheetImage, "Failed to get image from selected file");
        // We want to create the associated mutation
        const doNotCreateAssociatedMutation = false;
        const uploadedFile = await uploadAreaImageFile(
          sheetName,
          sectionAreaId,
          selectedRotation,
          crop,
          doNotCreateAssociatedMutation,
          pageNumber,
          sheetImage.file,
        );
        assert(uploadedFile, "Failed to upload area image sheet file");
        const sourcePdfInfo = await getSourcePdfUploadedData();
        await completeSheetUpload(sourcePdfInfo);
      }

      // We are not awaiting for the upload to complete because we want to return as soon as
      // possible and start the next sheet upload.
      performAreaImageSheetUpload();
    },
    [
      existingAreaId,
      uploadAreaImageFile,
      selectedRotation,
      crop,
      completeSheetUpload,
      extractImageFromFile,
    ],
  );

  /**
   * Creates an image sheet tiled from a PDF file.
   *
   * @param pdfFile - The PDF file to extract the image from.
   * @param resolution - The resolution in pixels of the image sheet tiled.
   * @param tilesSize - The size of the tiles in pixels.
   * @param sheetName - The name of the area and image sheet to create.
   * @param selectedPdfPage - The page number to extract from the PDF file.
   * @returns A promise that resolves when the image sheet tiled is created.
   */
  const createImageSheetTiled = useCallback(
    async (
      pdfFile: File,
      resolution: number,
      tilesSize: number,
      sheetName: string,
      selectedPdfPage: number,
      getSourcePdfUploadedData: GetSourcePdfFileData,
    ) => {
      // generate a new UUID as new area ID if existingAreaId does not exist
      const sectionAreaId = existingAreaId ?? generateGUID();
      const taskID = generateGUID();
      const controller = startTiledImageSheetGenerationTask(
        taskID,
        sheetName,
        sectionAreaId,
      );
      try {
        // Read the file content and parse it
        const projectTokenPromise = coreApi.getUserProjectToken(
          projectApi.projectId,
        );

        const selectedPageImageDataPromise = extractImageFromFile(
          pdfFile,
          selectedPdfPage,
        );

        const content = await pdfFile.arrayBuffer();
        const pdfDoc = await PDFJS.getDocument(content).promise;

        const { numPages } = pdfDoc;
        assert(selectedPdfPage <= numPages, "Invalid page requested");
        const page = await pdfDoc.getPage(selectedPdfPage);
        const finalRotation = page.rotate + selectedRotation;

        const totalNbOfTilesToUpload = computeNbOfAllTilesToUpload(
          page,
          resolution,
          tilesSize,
          finalRotation,
          crop,
        );
        imgSheetTiledUploadStep.current = 100 / totalNbOfTilesToUpload;
        nbTilesUploaded.current = 0;
        const allLevelsData: ImgSheetLevelsOfDetailNoLevel[] = [];
        const allTilesData: Array<{
          data: ImgSheetLevelsOfDetailSource[];
          scaledWidth: number;
          scaledHeight: number;
        }> = [];

        /**
         * Called when the tiles generation is completed for a specific level of detail.
         * It stores the generated tiles data along with their scaled dimensions.
         *
         * @param data The data for the current level of detail
         * @param scaledWidth The scaled width of the tiles
         * @param scaledHeight The scaled height of the tiles
         */
        function onTilesGenerationCompleted(
          data: ImgSheetLevelsOfDetailSource[],
          scaledWidth: number,
          scaledHeight: number,
        ): void {
          allTilesData.push({ data, scaledWidth, scaledHeight });
        }

        /**
         * Called when the extraction of a window is completed.
         *
         * @param x The x-coordinate of the window
         * @param y The y-coordinate of the window
         * @param currentResolution The current resolution of the window
         * @param extractedWindow The canvas element containing the extracted window
         * @param scaledWidth The scaled width of the window
         * @param scaledHeight The scaled height of the window
         * @param coordinator The TilesGenerationCoordinator to use for coordinating tile generation
         * @returns A promise that resolves when the extraction is completed
         */
        async function onWindowExtractionCompleted(
          x: number,
          y: number,
          currentResolution: number,
          extractedWindow: HTMLCanvasElement,
          scaledWidth: number,
          scaledHeight: number,
          coordinator: TilesGenerationCoordinator,
        ): Promise<void> {
          const extractedWindowResolution = Math.max(
            extractedWindow.width,
            extractedWindow.height,
          );
          if (extractedWindowResolution >= tilesSize) {
            const tilesData = generateTilesFromCanvas(
              extractedWindow,
              tilesSize,
              x * EXTRACTION_WINDOW_SIZE,
              y * EXTRACTION_WINDOW_SIZE,
              sectionAreaId,
              currentResolution,
              taskID,
              controller.signal,
              await projectTokenPromise,
              coordinator,
            ).then((data) =>
              onTilesGenerationCompleted(data, scaledWidth, scaledHeight),
            );
            tilesDataPromises.push(tilesData);
          } else if (x === 0 && y === 0 && currentResolution === resolution) {
            // If the first extracted window on the highest resolution is smaller than the tile size,
            // we are concluding that there will be no more additional extracted windows.
            // So we can just create an image sheet instead of image sheet tiled.
            return await createSheet(
              pdfFile,
              sheetName,
              selectedPdfPage,
              getSourcePdfUploadedData,
            );
          }
        }

        // This canvas will contain the image that will be split into multiple tiles
        // if the image is cropped, the canvas will contain the result after the crop
        // is applied. It is only used when the image resolution is smaller than the extraction window size.
        let canvasBeforeSplitIntoTiles: HTMLCanvasElement | undefined;
        const tilesDataPromises = new Array<Promise<void>>();
        const sequencer = tilesGenerationCoordinator.windowExtractionSequencer;
        for (
          let currentResolution = resolution;
          currentResolution >= tilesSize;
          currentResolution /= 2
        ) {
          const allTilesData: ImgSheetLevelsOfDetailSource[] = [];
          const {
            scale: currentScale,
            width: scaledWidth,
            height: scaledHeight,
          } = computeScaledSize(page, finalRotation, currentResolution);

          if (currentResolution > EXTRACTION_WINDOW_THRESHOLD) {
            const nbWindowsToExtractInRow = Math.ceil(
              (crop ? crop.width * 0.01 * scaledWidth : scaledWidth) /
                EXTRACTION_WINDOW_SIZE,
            );
            const nbWindowsToExtractInCol = Math.ceil(
              (crop ? crop.height * 0.01 * scaledHeight : scaledHeight) /
                EXTRACTION_WINDOW_SIZE,
            );

            const cropPositionX = crop ? crop.x * 0.01 * scaledWidth : 0;
            const cropPositionY = crop ? crop.y * 0.01 * scaledHeight : 0;
            const indexOffsetX = Math.floor(
              cropPositionX / EXTRACTION_WINDOW_SIZE,
            );
            const indexOffsetY = Math.floor(
              cropPositionY / EXTRACTION_WINDOW_SIZE,
            );
            const generationOffsetX = cropPositionX % EXTRACTION_WINDOW_SIZE;
            const generationOffsetY = cropPositionY % EXTRACTION_WINDOW_SIZE;

            for (let x = 0; x < nbWindowsToExtractInRow; x++) {
              for (let y = 0; y < nbWindowsToExtractInCol; y++) {
                if (controller.signal.aborted) {
                  throw new Error("Tile generation aborted");
                }

                await tilesGenerationCoordinator.getTilesParentCanvasGenerationPromise();

                // We want to wait until the previous window extraction's tiles generation is completed
                // before starting a new window extraction to avoid having too many memory consumption.
                getExtractionWindow(
                  page,
                  finalRotation,
                  currentScale,
                  EXTRACTION_WINDOW_SIZE,
                  x,
                  y,
                  indexOffsetX * EXTRACTION_WINDOW_SIZE + generationOffsetX,
                  indexOffsetY * EXTRACTION_WINDOW_SIZE + generationOffsetY,
                  sequencer,
                  crop,
                ).then((extractedWindow) =>
                  onWindowExtractionCompleted(
                    x,
                    y,
                    currentResolution,
                    extractedWindow,
                    scaledWidth,
                    scaledHeight,
                    tilesGenerationCoordinator,
                  ),
                );
              }
            }
          } else {
            await tilesGenerationCoordinator.getTilesParentCanvasGenerationPromise();

            if (canvasBeforeSplitIntoTiles === undefined) {
              canvasBeforeSplitIntoTiles = document.createElement("canvas");
              const canvasContext = canvasBeforeSplitIntoTiles.getContext("2d");
              assert(
                canvasContext,
                "Unable to create an offscreen 2d context to render the pdf image",
              );
              const scaledViewport = page.getViewport({
                scale: currentScale,
                rotation: finalRotation,
              });
              canvasBeforeSplitIntoTiles.width = scaledViewport.width;
              canvasBeforeSplitIntoTiles.height = scaledViewport.height;
              await page.render({ canvasContext, viewport: scaledViewport })
                .promise;
              canvasBeforeSplitIntoTiles = cropCanvas(
                canvasBeforeSplitIntoTiles,
                crop,
              );
            } else {
              canvasBeforeSplitIntoTiles = reduceCanvasSizeToHalf(
                canvasBeforeSplitIntoTiles,
              );
            }
            const canvasResolution = Math.max(
              canvasBeforeSplitIntoTiles.width,
              canvasBeforeSplitIntoTiles.height,
            );
            if (canvasResolution >= tilesSize) {
              const tilesData = generateTilesFromCanvas(
                canvasBeforeSplitIntoTiles,
                tilesSize,
                0,
                0,
                sectionAreaId,
                currentResolution,
                taskID,
                controller.signal,
                await projectTokenPromise,
                tilesGenerationCoordinator,
              ).then((data) =>
                onTilesGenerationCompleted(data, scaledWidth, scaledHeight),
              );
              tilesDataPromises.push(tilesData);
            } else if (
              allTilesData.length === 0 &&
              currentResolution === resolution
            ) {
              // If the first canvas resolution is smaller than the tile size,
              // and we still have no tiles generated, we should just create an image sheet instead of image sheet tiled.
              return await createSheet(
                pdfFile,
                sheetName,
                selectedPdfPage,
                getSourcePdfUploadedData,
              );
            }
          }

          if (controller.signal.aborted) {
            throw new Error("Tile generation aborted");
          }
        }

        await Promise.all(tilesDataPromises);

        // Merge the tiles data that have been generated for the same level of detail
        const allTilesDataByLevel = groupBy(
          allTilesData,
          (tileData) => `${tileData.scaledWidth}x${tileData.scaledHeight}`,
        );

        for (const tilesDataGroup of Object.values(allTilesDataByLevel)) {
          if (tilesDataGroup.length > 0) {
            // Get dimensions from the first item in the group (all items have same dimensions)
            const firstTile = tilesDataGroup[0];
            const dimX = Math.ceil(firstTile.scaledWidth / tilesSize);
            const dimY = Math.ceil(firstTile.scaledHeight / tilesSize);
            const dim = Math.max(dimX, dimY);

            // Sort the tiles so that they always have the same order (from top-left to bottom-right)
            const flatSortedTilesDataGroup = tilesDataGroup
              .flatMap((tile) => tile.data)
              .sort((tileA, tileB) => {
                // Sort by Y first, then by X
                if (tileA.y === tileB.y) {
                  return tileA.x - tileB.x;
                }
                return tileA.y - tileB.y;
              });

            allLevelsData.push({
              dimX: dim,
              dimY: dim,
              sources: flatSortedTilesDataGroup,
            });
          }
        }

        // This assert should never be triggered because at the first tile having a smaller resolution than the tile size,
        // we are creating an image sheet instead of an image sheet tiled.
        assert(
          allLevelsData.length > 0,
          "Failed to create the image sheet tiled. No tiles were generated",
        );
        const levelsOfDetail = convertToLevelsOfDetail(allLevelsData);
        const imgSheetTiledPayloadFile = new File(
          [`{"levelsOfDetail": ${JSON.stringify(levelsOfDetail)}}`],
          "imgSheetTiledDetails.json",
          { type: "application/json" },
        );

        if (controller.signal.aborted) {
          throw new Error("Tile generation aborted");
        }

        const extractedImageDataFromSelectedPage =
          await selectedPageImageDataPromise;

        /**
         * Uploads the area tiled image sheet with it's associated regular image sheet.
         */
        async function performAreaTiledImageSheetUpload(): Promise<void> {
          assert(
            extractedImageDataFromSelectedPage,
            "Failed to get image from user's selected page.",
          );

          // First upload the regular image sheet
          // We don't want to create the associated mutation for the regular image sheet
          const doNotCreateAssociatedMutation = true;
          const uploadedRegularImageSheetFile = await uploadAreaImageFile(
            sheetName,
            sectionAreaId,
            selectedRotation,
            crop,
            doNotCreateAssociatedMutation,
            selectedPdfPage,
            extractedImageDataFromSelectedPage.file,
          );
          assert(
            uploadedRegularImageSheetFile,
            "Failed to upload area image sheet file before tiled upload",
          );
          const sheetImageFile = await transformImage(
            extractedImageDataFromSelectedPage.file,
            finalRotation,
            crop,
          );
          const thumbnailFile = await resizeImage(sheetImageFile);
          const { width: pixelWidth, height: pixelHeight } =
            await parseImageFromFile(extractedImageDataFromSelectedPage.file);

          // These lines compute the actual width/height, that the image data ends up taking on the 100m x 100m tiled sheet.
          // For this we need to multiply the scale factor of the sheet (emulated by calling computeScaledSize with 1 as the size),
          // And then apply the crop as needed.
          const { width: scaledWidth, height: scaledHeight } =
            computeScaledSize(page, finalRotation, 1);
          const widthRatioAfterCrop =
            scaledWidth * (crop ? crop.width * 0.01 : 1);
          const heightRatioAfterCrop =
            scaledHeight * (crop ? crop.height * 0.01 : 1);

          await uploadAreaImageSheetTiled(
            sheetName,
            sectionAreaId,
            {
              file: imgSheetTiledPayloadFile,
              tilesSize,
              associatedRegularImageSheetInfo: {
                url: uploadedRegularImageSheetFile.downloadUrl,
                pixelWidth,
                pixelHeight,
                md5Hash: uploadedRegularImageSheetFile.md5,
                fileName: sheetName,
                relativePoseToSheet: {
                  pos: {
                    x: 0,
                    y: 0,
                    // The image data is placed on the top left of the tiled sheet.
                    // We need to offset the fallback sheet to be placed at the top-left corner of the tiled 100m square.
                    //
                    //        +-----------+--+
                    //        |  fallback |  |
                    //        +-----------+--+
                    //        |              |  <- tiled sheet (100m x 100m)
                    //        +--------------+
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    z: -100 * heightRatioAfterCrop,
                  },
                  // The Project API assumes that the width of the low res sheet is 100m, same as the width of a tiled sheet.
                  // If we occupy a smaller space, we also need to scale to lowResSheet down.
                  scale: {
                    x: widthRatioAfterCrop,
                    y: widthRatioAfterCrop,
                    z: widthRatioAfterCrop,
                  },
                  isWorldRot: false,
                  rot: null,
                  gps: null,
                },
              },
            },
            thumbnailFile,
            undefined,
            selectedPdfPage,
          );
          completeTiledImageSheetGenerationTask(taskID);
          const sourcePdfInfo = await getSourcePdfUploadedData();
          completeSheetUpload(sourcePdfInfo);
        }

        // We are not awaiting for the upload to complete because we want to return as soon as
        // possible and start the next sheet upload.
        performAreaTiledImageSheetUpload();
      } catch (error) {
        if (controller.signal.aborted) {
          cancelTiledImageSheetGenerationTask(taskID);
          // If the task was cancelled, we don't want to stay in the importSheet mode
          if (selectModeName(store.getState()) === "importSheet") {
            dispatch(changeMode("sheet"));
          }
          return;
        }
        failTiledImageSheetGenerationTask(
          taskID,
          "Failed to create image sheet tiled",
        );
        handleErrorWithDialog({
          title: "Image sheet tiled upload",
          error,
        });
        // If the task failed, we don't want to stay in the importSheet mode
        if (selectModeName(store.getState()) === "importSheet") {
          dispatch(changeMode("sheet"));
        }
      }
    },
    [
      completeSheetUpload,
      createSheet,
      crop,
      existingAreaId,
      generateTilesFromCanvas,
      selectedRotation,
      uploadAreaImageFile,
      uploadAreaImageSheetTiled,
      startTiledImageSheetGenerationTask,
      completeTiledImageSheetGenerationTask,
      failTiledImageSheetGenerationTask,
      cancelTiledImageSheetGenerationTask,
      handleErrorWithDialog,
      dispatch,
      extractImageFromFile,
      coreApi,
      projectApi.projectId,
      tilesGenerationCoordinator,
    ],
  );

  // Cancel the current file upload and stop the workflow
  const cancelUpload = useCallback(() => {
    for (const { id } of tasks) {
      singleFileUploadManager.cancelFileUpload(id);
    }
    resetToInitialState();
    return Promise.resolve();
  }, [resetToInitialState, tasks, singleFileUploadManager]);

  // Set a custom floor image file
  const setFile = useCallback(
    (file: File) => {
      setSelectedFile(file);
      setSelectedPdfPage(1);

      try {
        const fileName = file.name;
        if (isSupportedImgSheetFileExtension(fileName)) {
          extractPreviewImage(file);
          if (
            !inputName.length ||
            inputName === removeExtension(previewImageFile?.name)
          ) {
            setInputName(removeExtension(fileName));
          }
        } else {
          handleErrorWithToast({
            title: "Failed to import file",
            error: "The file extension is not supported.",
          });
        }
      } catch (error) {
        handleErrorWithDialog({ title: "Image sheet upload", error });
      }
    },
    [
      handleErrorWithDialog,
      handleErrorWithToast,
      inputName,
      extractPreviewImage,
      previewImageFile?.name,
      setInputName,
    ],
  );

  // If a file was provided from the import data dialog, set it as the selected file on startup.
  useEffect(() => {
    if (sheetFile) {
      setFile(sheetFile);
    }
  }, [sheetFile, setFile]);

  // Load the default grid as the floor image
  const setDefaultGrid = useCallback(() => {
    if (!defaultGrid) return;
    setFile(defaultGrid);
    setInputName(inputName);
    setInputNameError(undefined);
  }, [defaultGrid, inputName, setFile, setInputName]);

  return {
    selectedFile,
    previewImageFile,
    inputName,
    inputNameError,
    hasDefaultGrid: !!defaultGrid,
    isImageOversized,
    setFile,
    setInputName,
    cancelUpload,
    createImageSheetTiled,
    createSheet,
    setDefaultGrid,
    widthToHeightRatio,
    rotation: selectedRotation,
    setRotation: setSelectedRotation,
    crop,
    setCrop,
    numberOfPdfPages,
    onUpdateSelectedPdfPage,
    selectedPdfPage,
    existingArea,
  };
}
